/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectVehicleComponentForVehicleSystem.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Autocomplete, TextField, Typography, IconButton, CircularProgress } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import { HandleApiActions, labCar } from "../../redux/actions";

const normalizeSelectId = (id) => {
  if (id === null || id === undefined || id === "") return "";

  const numericId = Number(id);
  return Number.isNaN(numericId) ? id : numericId;
};

function SelectVehicleComponentForVehicleSystem({
  open = false,
  projectId,
  variantId,
  isZonal = false,
  zonePosition,
  getSelectedVehicleSystem,
  getSelectedVehicleComponent,
  setVcIdError,
  vcId = null,
  vsId = null,
}) {
  const dispatch = useDispatch();
  const [vehicleSystemList, setVehicleSystemList] = useState([]);
  const [vehicleComponentList, setVehicleComponentList] = useState([]);
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState("");
  const [selectedVehicleComponent, setSelectedVehicleComponent] = useState("");
  const [loadingVehicleSystems, setLoadingVehicleSystems] = useState(false);
  const [loadingVehicleComponents, setLoadingVehicleComponents] =
    useState(false);
  const normalizedVsId = useMemo(() => normalizeSelectId(vsId), [vsId]);
  const normalizedVcId = useMemo(() => normalizeSelectId(vcId), [vcId]);

  const vehicleSystemOptions = useMemo(
    () =>
      vehicleSystemList.map((item) => ({
        value: Number(item.id),
        label:
          item.name ||
          item.description ||
          item.assembly_number ||
          `Vehicle System ${item.id}`,
        description: item.description || "",
        zoneName: item?.zone_details?.[0]?.zone_name || "",
      })),
    [vehicleSystemList],
  );

  const vehicleComponentOptions = useMemo(
    () =>
      vehicleComponentList.map((item) => ({
        value: Number(item.id),
        label:
          item.name ||
          item.description ||
          item.component_number ||
          `Vehicle Component ${item.id}`,
        description: item.description || "",
        zoneName: item?.zone_details?.[0]?.zone_name || "",
      })),
    [vehicleComponentList],
  );

  useEffect(() => {
    if (!open) {
      setSelectedVehicleSystem("");
      setSelectedVehicleComponent("");
      setVehicleComponentList([]);
      setVehicleSystemList([]);
      getSelectedVehicleSystem?.("");
      getSelectedVehicleComponent?.("");
      setVcIdError?.(false);
      return;
    }

    const nextVehicleComponent = normalizedVsId ? normalizedVcId : "";

    setSelectedVehicleSystem(normalizedVsId);
    setSelectedVehicleComponent(nextVehicleComponent);
    setVehicleComponentList([]);
    getSelectedVehicleSystem?.(normalizedVsId);
    getSelectedVehicleComponent?.(nextVehicleComponent);
    setVcIdError?.(false);
  }, [
    open,
    projectId,
    variantId,
    isZonal,
    zonePosition,
    normalizedVsId,
    normalizedVcId,
    getSelectedVehicleSystem,
    getSelectedVehicleComponent,
    setVcIdError,
  ]);

  useEffect(() => {
    let isActive = true;

    const getVehicleSystemForProject = async () => {
      if (!open || !projectId || !variantId) {
        setVehicleSystemList([]);
        setLoadingVehicleSystems(false);
        return;
      }

      if (isZonal && (zonePosition === null || zonePosition === undefined)) {
        setVehicleSystemList([]);
        setLoadingVehicleSystems(false);
        return;
      }

      setLoadingVehicleSystems(true);

      try {
        const vehicleSystemResponse = await dispatch(
          HandleApiActions({
            ...labCar.get_vehicle_systems,
            show_toast: false,
            params: {
              project_id: Number(projectId),
              variant_id: Number(variantId),
              page_size: 1000,
              page_number: 1,
            },
          }),
        );

        if (!isActive) return;

        setVehicleSystemList(
          vehicleSystemResponse?.error ? [] : vehicleSystemResponse?.data || [],
        );
      } finally {
        if (isActive) {
          setLoadingVehicleSystems(false);
        }
      }
    };

    getVehicleSystemForProject();

    return () => {
      isActive = false;
    };
  }, [dispatch, open, projectId, variantId, isZonal, zonePosition]);

  useEffect(() => {
    let isActive = true;

    const getVehicleComponentForSelectedSystem = async () => {
      // Wait for the vehicle-system reset (first effect) to apply before
      // evaluating preselect logic, otherwise selectedVehicleSystem is
      // still "" and we'd wrongly clear the preselected vehicle component.
      if (selectedVehicleSystem === "" && normalizedVsId !== "") {
        return;
      }

      const shouldUsePreselectedComponent =
        normalizedVsId !== "" &&
        String(selectedVehicleSystem) === String(normalizedVsId) &&
        normalizedVcId !== "";
      const nextVehicleComponent = shouldUsePreselectedComponent
        ? normalizedVcId
        : "";

      setSelectedVehicleComponent(nextVehicleComponent);
      getSelectedVehicleComponent?.(nextVehicleComponent);
      setVcIdError?.(false);

      if (!open || !projectId || !variantId || !selectedVehicleSystem) {
        setVehicleComponentList([]);
        setLoadingVehicleComponents(false);
        return;
      }

      if (isZonal && (zonePosition === null || zonePosition === undefined)) {
        setVehicleComponentList([]);
        setLoadingVehicleComponents(false);
        return;
      }

      setLoadingVehicleComponents(true);

      try {
        const vehicleComponentResponse = await dispatch(
          HandleApiActions({
            ...labCar.get_vehicle_components,
            show_toast: false,
            params: {
              project_id: Number(projectId),
              variant_id: Number(variantId),
              vs_id: Number(selectedVehicleSystem),
              page_size: 1000,
              page_number: 1,
            },
          }),
        );

        if (!isActive) return;

        setVehicleComponentList(
          vehicleComponentResponse?.error
            ? []
            : vehicleComponentResponse?.data || [],
        );
      } finally {
        if (isActive) {
          setLoadingVehicleComponents(false);
        }
      }
    };

    getVehicleComponentForSelectedSystem();

    return () => {
      isActive = false;
    };
  }, [
    dispatch,
    open,
    projectId,
    variantId,
    selectedVehicleSystem,
    isZonal,
    zonePosition,
    normalizedVsId,
    normalizedVcId,
    getSelectedVehicleComponent,
    setVcIdError,
  ]);

  // ---- Shared tooltip content builder ----
  const renderInfoTooltip = (option) => {
    if (!option?.description && !option?.zoneName) return null;

    return (
      <Box sx={{ p: 0.5 }}>
        {option.zoneName ? (
          <Typography variant="caption" display="block">
            {`ZONE : ${option.zoneName}`}
          </Typography>
        ) : null}
        <Typography variant="caption" display="block">
          {`Description : ${option.description || "-"}`}
        </Typography>
      </Box>
    );
  };

  // ---- Shared option row renderer (label + info icon) ----
  const renderOptionRow = (props, option) => (
    <Box
      component="li"
      {...props}
      key={option.value}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option.label}
      </Box>

      {renderInfoTooltip(option) && (
        <CustomToolTip type="component" title={renderInfoTooltip(option)}>
          <IconButton
            size="small"
            sx={{ color: "black", p: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <InfoOutlinedIcon sx={{ fontSize: "15px" }} />
          </IconButton>
        </CustomToolTip>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ mb: 0.5, color: "#6f7174", fontSize: "0.9rem", fontWeight: 500 }}>
          Select Vehicle System
        </Typography>
        <Autocomplete
          fullWidth
          disablePortal
          options={vehicleSystemOptions}
          getOptionLabel={(option) => option?.label || ""}
          value={
            vehicleSystemOptions.find(
              (opt) => opt.value === selectedVehicleSystem,
            ) || null
          }
          onChange={(event, newValue) => {
            const nextVehicleSystem = newValue ? newValue.value : "";
            setSelectedVehicleSystem(nextVehicleSystem);
            getSelectedVehicleSystem?.(nextVehicleSystem);
          }}
          disabled={!open || loadingVehicleSystems}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select Vehicle System"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingVehicleSystems ? (
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={renderOptionRow}
        />
      </Box>

      <Box sx={{ width: "100%" }}>
        <Typography sx={{ mb: 0.5, color: "#6f7174", fontSize: "0.9rem", fontWeight: 500 }}>
          Select Vehicle Component
        </Typography>
        <Autocomplete
          fullWidth
          disablePortal
          options={vehicleComponentOptions}
          getOptionLabel={(option) => option?.label || ""}
          value={
            vehicleComponentOptions.find(
              (opt) => opt.value === selectedVehicleComponent,
            ) || null
          }
          onChange={(event, newValue) => {
            const nextVehicleComponent = newValue ? newValue.value : "";
            setSelectedVehicleComponent(nextVehicleComponent);
            getSelectedVehicleComponent?.(nextVehicleComponent);
            setVcIdError?.(!nextVehicleComponent);
          }}
          disabled={!open || !selectedVehicleSystem || loadingVehicleComponents}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select Vehicle Component"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingVehicleComponents ? (
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={renderOptionRow}
        />
      </Box>
    </Box>
  );
}

export default SelectVehicleComponentForVehicleSystem;